import { autocompletion } from "@codemirror/autocomplete";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { ErrorBaseInfo } from "src/api/types";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { Icon } from "src/base-components/Icon";
import { DatabaseConnectionForm } from "src/databaseConnectionNode/types";
import { Callout } from "src/design-system/Callout";
import { Tooltip } from "src/design-system/Tooltip";

type Props = {
  immutable: boolean;
  isReactive: boolean;
  displayedError?: ErrorBaseInfo;
  isSQLProvider: boolean;
};

const DangerousSQLKeywords = [
  "alter",
  "create",
  "drop",
  "truncate",
  "delete",
  "update",
  "replace",
  "grant",
  "revoke",
  "rename",
  "call",
  "merge",
  "set",
  "start transaction",
  "commit",
  "rollback",
  "savepoint",
  "release",
  "copy",
  "vacuum",
  "analyze",
  "cluster",
  "reindex",
  "load",
  "unload",
  "backup",
  "restore",
];

const DangerousNoSQLKeywords = [
  "$where",
  "$function",
  "update",
  "delete",
  "killCursors",
  "findAndModify",
  "endSessions",
  "drop",
  "abort",
];

export const isDangerousSQLQuery = (query: string) => {
  return DangerousSQLKeywords.some((keyword) =>
    query?.toLowerCase().includes(keyword),
  );
};
export const isDangerousNoSQLQuery = (query: string) => {
  return DangerousNoSQLKeywords.some((keyword) =>
    query?.toLowerCase().includes(keyword),
  );
};

const ExampleQueriesTooltipBody: React.FC<{ isSQLProvider: boolean }> = ({
  isSQLProvider,
}) => (
  <div>
    Once you have introduced a variable (e.g.,{" "}
    <code>{isSQLProvider ? "$name" : "%name"}</code>) in the Variables section
    above, you can reference it within the {isSQLProvider ? "SQL" : ""} query
    using the {isSQLProvider ? "Dollar" : "Percent"} sign (
    <code>{isSQLProvider ? "$" : "%"}</code>) prefix:
    <br />
    <br />
    {isSQLProvider ? (
      <code className="whitespace-pre">
        {`SELECT *
FROM my_table
WHERE name = $name;`}
      </code>
    ) : (
      <code className="whitespace-pre">
        {`{
  "find": "my_collection",
  "filter": {
    "name": %name
  }
}`}
      </code>
    )}
  </div>
);

export const QueryEditor: React.FC<Props> = ({
  immutable,
  isReactive,
  displayedError,
  isSQLProvider,
}) => {
  const { setValue, watch } = useFormContext<DatabaseConnectionForm>();
  const src = watch("query.expression");

  const isDangerousSQL = useMemo(
    () => isSQLProvider && isDangerousSQLQuery(src),
    [src, isSQLProvider],
  );
  const isDangerousNoSQL = useMemo(
    () => !isSQLProvider && isDangerousNoSQLQuery(src),
    [src, isSQLProvider],
  );

  const onCodeChange = useCallback(
    (codeText: string | undefined) => {
      if (!isReactive && src !== codeText) {
        setValue("query.expression", codeText ?? "");
      }
    },
    [isReactive, src, setValue],
  );

  const errorHighlightedLine =
    displayedError?.loc_type === "line" &&
    typeof displayedError?.loc === "number"
      ? displayedError?.loc
      : undefined;

  const COMPLETION_EXTENSION = autocompletion({
    override: [() => null],
  });

  return (
    <>
      <span className="text-gray-500 font-inter-normal-12px">
        You can embed variables within your {isSQLProvider ? "SQL" : ""} query
        using{" "}
        <span className="font-fira-code rounded bg-gray-100 px-1">
          {isSQLProvider ? "$" : "%"}
        </span>{" "}
        notation
        <Tooltip
          body={<ExampleQueriesTooltipBody isSQLProvider={isSQLProvider} />}
          placement="top"
        >
          <Icon
            color="text-gray-500"
            cursorType="pointer"
            icon={faQuestionCircle}
            size="xs"
          />
        </Tooltip>
      </span>
      <div className="mt-3 h-80">
        <CodeEditor
          completionExtension={COMPLETION_EXTENSION}
          dataLoc="code-editor"
          disabled={immutable}
          errorHighlightedLine={errorHighlightedLine}
          language={isSQLProvider ? "sql" : "mongodb"}
          value={src}
          onChange={onCodeChange}
        />
      </div>
      {(isDangerousSQL || isDangerousNoSQL) && (
        <div className="mt-3">
          <Callout type="warning">
            Warning: This query could modify or delete data, or alter the
            database. Please verify its intent and ensure you have necessary
            backups to prevent unintended effects.
          </Callout>
        </div>
      )}
    </>
  );
};
